import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MatterService from '../../../../../AppSettings/services/MatterService';
import MilestoneService, {
  NoMilestoneError,
} from '../../../../../AppSettings/services/MilestoneService';
import ToastService from '../../../../../AppSettings/services/ToastService';
import { isLegalEnvironment } from '../../../../../AppSettings/environmentConfig';
import useProjectDueDate from '@frontend/AppSettings/hooks/useProjectDueDate';
import SectionCard from '@frontend/Components/SectionCard/SectionCard';
import { GetMatterByIdResponse } from '@frontend/Models/Matter/MatterRequestResponseModels';
import './MatterInfo.css';

type MatterField = {
  label: string;
  value: string;
  variant?: 'pill';
  key?: keyof GetMatterByIdResponse | string;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  // Calendar-only strings (YYYY-MM-DD) are built as local dates so they don't
  // render as the previous day in negative UTC offsets.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
};

const displayValue = (value?: string | null) =>
  value && String(value).trim() ? String(value) : '—';

const MatterInfo: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const isLegalEnv = isLegalEnvironment;
  const projectDueDate = useProjectDueDate(!isLegalEnv ? matterId : null);
  const queryClient = useQueryClient();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [dueDateDraft, setDueDateDraft] = useState('');

  const {
    data: matter,
    isPending,
    error,
  } = useQuery<GetMatterByIdResponse>({
    queryKey: ['Matters', matterId],
    queryFn: () => MatterService.getMatterDetails(matterId!),
    enabled: !!matterId,
  });

  const updateMatterMutation = useMutation({
    mutationFn: (updateData: Record<string, string>) =>
      MatterService.updateMatter(matterId!, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Matters', matterId!] });
      queryClient.invalidateQueries({ queryKey: ['MatterName', matterId!] });
      setEditingKey(null);
      setEditValue('');
    },
    onError: (err: Error) => {
      console.error('Error updating matter:', err);
      alert('Failed to update. Please try again.');
    },
  });

  // A project's due date has no stored column; it is the latest milestone's
  // planned date. Persist edits by updating that milestone (same path the
  // project list uses) and refresh the derived due-date query.
  const updateDueDateMutation = useMutation({
    mutationFn: (date: string) =>
      MilestoneService.setProjectDueDate(matterId!, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectDueDate', matterId] });
      setIsEditingDueDate(false);
      setDueDateDraft('');
      ToastService.success('Due date updated successfully');
    },
    onError: (err: Error) => {
      setIsEditingDueDate(false);
      if (err instanceof NoMilestoneError) {
        ToastService.error(
          'Add a milestone to this project before setting a due date.'
        );
        return;
      }
      console.error('Error updating due date:', err);
      ToastService.error('Failed to update the due date. Please try again.');
    },
  });

  const startDueDateEdit = () => {
    setDueDateDraft(projectDueDate || '');
    setIsEditingDueDate(true);
  };

  const cancelDueDateEdit = () => {
    setIsEditingDueDate(false);
    setDueDateDraft('');
  };

  const saveDueDate = () => {
    const next = dueDateDraft;
    if (!next || next === (projectDueDate || '')) {
      cancelDueDateEdit();
      return;
    }
    updateDueDateMutation.mutate(next);
  };

  const getRawValue = (key?: string) => {
    if (!key) return '';
    if (key === 'description') {
      return (
        (matter as any)?.description ||
        (matter as any)?.summary ||
        (matter as any)?.details ||
        ''
      );
    }
    const raw = (matter as any)?.[key];
    return raw ?? '';
  };

  const startEdit = (key?: string) => {
    if (!key) return;
    setEditingKey(key);
    const raw = getRawValue(key);
    setEditValue(raw ? String(raw) : '');
  };

  const saveEdit = () => {
    if (!editingKey) return;
    const currentRaw = getRawValue(editingKey);
    let nextValue = editValue ?? '';
    if (editingKey === 'mattername') {
      nextValue = nextValue.trim();
      if (!nextValue) {
        cancelEdit();
        return;
      }
    }
    if (
      String(currentRaw) === String(nextValue) ||
      (editingKey === 'mattername' && String(currentRaw).trim() === nextValue)
    ) {
      cancelEdit();
      return;
    }
    updateMatterMutation.mutate({ [editingKey]: nextValue });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  const cardTitle = isLegalEnv ? 'Matter Information' : 'Project Information';
  const toggleDescription = () => {
    setIsDescriptionOpen((prev) => {
      if (prev && isDescriptionEditing) {
        cancelEdit();
      }
      return !prev;
    });
  };
  const handleDescriptionCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (isDescriptionEditing) return;
    if (isDescriptionOpen && target.closest('.matter-description-body')) {
      return;
    }
    toggleDescription();
  };
  const handleDescriptionCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isDescriptionEditing) {
        toggleDescription();
      }
    }
  };

  const columns: MatterField[][] = [
    [
      ...(!isLegalEnv
        ? [
            {
              label: 'Project Name',
              value: displayValue(matter?.mattername),
              key: 'mattername',
            },
          ]
        : []),
      {
        label: 'Client',
        value: displayValue(matter?.client_name),
        key: 'client_name',
      },
      {
        label: 'Client Phone',
        value: displayValue((matter as any)?.client_phone),
        key: 'client_phone',
      },
      {
        label: 'Client Email',
        value: displayValue((matter as any)?.client_email),
        key: 'client_email',
      },
    ],
    [
      {
        label: 'Creation Date',
        value: formatDate(
          matter?.opendate || (matter as any)?.created_at || (matter as any)?.createdAt
        ),
      },
      ...(!isLegalEnv
        ? [
            {
              label: 'Due Date',
              value: projectDueDate ? formatDate(projectDueDate) : '—',
            },
          ]
        : []),
      {
        label: isLegalEnv ? 'Practice Area' : 'Area',
        value: displayValue(
          (matter as any)?.practice_area ||
            (matter as any)?.practicearea ||
            matter?.country
        ),
        key: (matter as any)?.practice_area ? 'practice_area' : 'country',
      },
      {
        label: 'LSD',
        value: displayValue((matter as any)?.registration_no),
        key: isLegalEnv ? undefined : 'registration_no',
      },
    ],
    [
      {
        label: isLegalEnv ? 'Matter Type' : 'Job Number',
        value: displayValue(isLegalEnv ? matter?.mattertype : (matter as any)?.application_no),
        key: isLegalEnv ? 'mattertype' : 'application_no',
      },
      {
        label: 'Workflow',
        value: displayValue((matter as any)?.workflow),
      },
      {
        label: 'AFE',
        value: displayValue((matter as any)?.publication_no),
        key: isLegalEnv ? undefined : 'publication_no',
      },
      {
        label: 'Priority',
        value: displayValue((matter as any)?.priority),
        variant: 'pill',
      },
    ],
  ];

  const descriptionValue = displayValue(getRawValue('description'));
  const descriptionKey = 'description';
  const isDescriptionEditing = editingKey === descriptionKey;

  const renderField = ({ label, value, variant, key }: MatterField) => {
    // The project due date is editable but persists through MilestoneService
    // (not the generic matter update), so it gets a dedicated date editor.
    if (label === 'Due Date') {
      return (
        <div key={label} className="matter-summary-field">
          <div className="matter-summary-label">{label}</div>
          <div
            className="matter-summary-value is-editable"
            onDoubleClick={startDueDateEdit}
            title="Double-click to edit due date"
          >
            {isEditingDueDate ? (
              <input
                type="date"
                className="matter-summary-input"
                value={dueDateDraft}
                onChange={(e) => setDueDateDraft(e.target.value)}
                onBlur={saveDueDate}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveDueDate();
                  if (e.key === 'Escape') cancelDueDateEdit();
                }}
                disabled={updateDueDateMutation.isPending}
                autoFocus
              />
            ) : (
              value
            )}
          </div>
        </div>
      );
    }

    const isEditing = key && editingKey === key;
    const isEditable = !!key && variant !== 'pill' && label !== 'Creation Date';

    return (
      <div key={label} className="matter-summary-field">
        <div className="matter-summary-label">{label}</div>
        <div
          className={`matter-summary-value ${
            variant === 'pill' && value !== '—' ? 'is-pill' : ''
          } ${isEditable ? 'is-editable' : ''}`}
          onDoubleClick={() => startEdit(key)}
        >
          {isEditable && isEditing ? (
            <input
              className="matter-summary-input"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={onKeyDown}
              autoFocus
            />
          ) : (
            value
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <SectionCard
        title={cardTitle}
        className="matter-summary-card"
      >
        {isPending && (
          <div className="matter-summary-status">Loading matter details…</div>
        )}
        {error && (
          <div className="matter-summary-status error">
            Error loading matter details
          </div>
        )}
        {!isPending && !error && (
          <div className="matter-summary-grid matter-summary-grid--three">
            {columns.map((col, idx) => (
              <div key={idx} className="matter-summary-column">
                {col.map(renderField)}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {!isPending && !error && (
        <div
          className={`matter-description-wrapper ${
            !isDescriptionOpen ? 'is-collapsed' : ''
          }`}
          onClick={handleDescriptionCardClick}
          role="button"
          tabIndex={0}
          aria-expanded={isDescriptionOpen}
          onKeyDown={handleDescriptionCardKeyDown}
        >
          <SectionCard
            title="Description"
            className={`matter-summary-card matter-summary-card--spaced matter-summary-card--description ${
              !isDescriptionOpen ? 'is-collapsed' : ''
            }`}
            right={
              <div className="matter-header-actions">
                <button
                  type="button"
                  className="matter-toggle-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDescription();
                  }}
                  aria-expanded={isDescriptionOpen}
                  aria-label={`${isDescriptionOpen ? 'Collapse' : 'Expand'} description`}
                >
                  <i className={`fas fa-chevron-${isDescriptionOpen ? 'up' : 'down'}`}></i>
                </button>
                <button
                  type="button"
                  className="matter-edit-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEdit(descriptionKey);
                  }}
                  title="Edit description"
                >
                  <i className="fas fa-pen"></i>
                </button>
              </div>
            }
          >
            {isDescriptionOpen && (
              <div
                className={`matter-description-body ${
                  descriptionValue !== '—' ? '' : 'is-empty'
                } is-editable`}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  startEdit(descriptionKey);
                }}
              >
                {isDescriptionEditing ? (
                  <textarea
                    className="matter-description-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={saveEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        saveEdit();
                      } else if (e.key === 'Escape') {
                        cancelEdit();
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span>{descriptionValue}</span>
                )}
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </>
  );
};

export default MatterInfo;
